import { useCallback, useEffect, useRef, useState } from 'react';
import { USER_CONTEXT } from '../../../../../constants';
import store, { showToast, showToastWithType } from '../../../../core/store';
import { fetchCasePlanEditors, patchCasePlanEditors } from '../../api/casePlanDetailApi';
import {
  CASE_PLAN_EDITORS_LOAD_FAILED_MESSAGE,
  CASE_PLAN_EDITORS_UPDATE_FAILED_MESSAGE,
  CASE_PLAN_EDITORS_UPDATE_SUCCESS_MESSAGE,
} from '../../constants/reentry-planning.constants';
import { mapActiveEditorToChip, mapEditorsToIds } from '../../utils/case-plan-editors.util';

const mapEditorsFromResponse = (editors) =>
  (Array.isArray(editors) ? editors : [])
    .map((editor) => mapActiveEditorToChip(editor, USER_CONTEXT?.personId))
    .filter(Boolean);

export const useEditorsPlanModalHook = ({
  open = false,
  planId,
  onClose,
  onSaved,
} = {}) => {
  const modalRef = useRef(null);
  const closingFromUserRef = useRef(false);
  const wasOpenRef = useRef(false);
  const [editors, setEditors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const resetLocalState = useCallback(() => {
    setEditors([]);
    setIsLoading(false);
    setIsSaving(false);
    setHasLoaded(false);
  }, []);

  const handleClose = useCallback(() => {
    closingFromUserRef.current = true;
    if (modalRef.current?.toggleModal) {
      modalRef.current.toggleModal();
    }
    resetLocalState();
    onClose?.();
  }, [onClose, resetLocalState]);

  useEffect(() => {
    if (!open) {
      resetLocalState();
      return undefined;
    }

    if (!planId) {
      return undefined;
    }

    const abortController = new AbortController();

    const loadEditors = async () => {
      setIsLoading(true);
      try {
        const response = await fetchCasePlanEditors(planId, abortController.signal);
        if (abortController.signal.aborted) {
          return;
        }
        setEditors(mapEditorsFromResponse(response?.editors));
        setHasLoaded(true);
      } catch (error) {
        if (abortController.signal.aborted || error?.name === 'AbortError') {
          return;
        }
        store.dispatch(showToast(CASE_PLAN_EDITORS_LOAD_FAILED_MESSAGE));
        setEditors([]);
        setHasLoaded(false);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadEditors();

    return () => {
      abortController.abort();
    };
  }, [open, planId, resetLocalState]);

  const syncModal = useCallback(() => {
    if (!modalRef.current?.toggleModal) {
      return;
    }

    if (open) {
      closingFromUserRef.current = false;
      wasOpenRef.current = true;
      modalRef.current.toggleModal();
      return;
    }

    if (wasOpenRef.current && !closingFromUserRef.current) {
      wasOpenRef.current = false;
      if (modalRef.current?.toggleModal) {
        modalRef.current.toggleModal();
      }
    } else {
      wasOpenRef.current = false;
      closingFromUserRef.current = false;
    }
  }, [open]);

  useEffect(syncModal, [syncModal]);

  const handleEditorsChange = useCallback((nextEditors) => {
    setEditors(Array.isArray(nextEditors) ? nextEditors : []);
  }, []);

  const handleSave = useCallback(async () => {
    if (!planId || isSaving || !hasLoaded) {
      return;
    }

    setIsSaving(true);
    try {
      await patchCasePlanEditors(planId, { editorIds: mapEditorsToIds(editors) });
      store.dispatch(showToastWithType(CASE_PLAN_EDITORS_UPDATE_SUCCESS_MESSAGE, 'success'));
      resetLocalState();
      onSaved?.();
      onClose?.();
    } catch {
      store.dispatch(showToast(CASE_PLAN_EDITORS_UPDATE_FAILED_MESSAGE));
    } finally {
      setIsSaving(false);
    }
  }, [editors, hasLoaded, isSaving, onClose, onSaved, planId, resetLocalState]);

  return {
    modalRef,
    editors,
    isLoading,
    isSaving,
    canSave: hasLoaded && !isLoading && !isSaving,
    handleEditorsChange,
    handleClose,
    handleSave,
  };
};
